/**
 * Subscription.js
 * DataQuad Subscription Page with Paddle Billing Integration
 * 
 * Features:
 * - Plan selection (Standard, Pro, Max)
 * - Monthly/Annual billing toggle
 * - Paddle.js checkout overlay
 * - User authentication before checkout
 * - Subscription management link
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  ArrowLeft,
  Check,
  Star,
  Infinity,
  Database,
  Zap,
  Clock,
  CreditCard,
  Lock,
  Shield,
  CheckCircle2,
  AlertTriangle,
  User,
  Mail,
  Eye,
  EyeOff,
  ChevronRight,
  Globe,
  Building2,
  Sparkles
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';

// ============================================================================
// PADDLE CONFIGURATION
// ============================================================================

// Paddle client-side token - get from Paddle Dashboard > Developer Tools > Authentication
const PADDLE_CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN || 'test_xxxxxxxxxxxxxxxxxxxxxxx';

// Environment: 'sandbox' for testing, 'production' for live
const PADDLE_ENVIRONMENT = process.env.REACT_APP_PADDLE_ENVIRONMENT || (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox');

console.log('Paddle Environment:', PADDLE_ENVIRONMENT, 'ENV VAR:', process.env.REACT_APP_PADDLE_ENVIRONMENT);


// Paddle Price IDs - get from Paddle Dashboard > Catalog > Prices
// Replace these with your actual price IDs
const PADDLE_PRICES = {
  standard: {
    monthly: process.env.REACT_APP_PADDLE_PRICE_STANDARD_MONTHLY || 'pri_01abc123standard_monthly',
    annual: process.env.REACT_APP_PADDLE_PRICE_STANDARD_ANNUAL || 'pri_01abc123standard_annual',
  },
  pro: {
    monthly: process.env.REACT_APP_PADDLE_PRICE_PRO_MONTHLY || 'pri_01abc123pro_monthly',
    annual: process.env.REACT_APP_PADDLE_PRICE_PRO_ANNUAL || 'pri_01abc123pro_annual',
  },
  max: {
    monthly: process.env.REACT_APP_PADDLE_PRICE_MAX_MONTHLY || 'pri_01abc123max_monthly',
    annual: process.env.REACT_APP_PADDLE_PRICE_MAX_ANNUAL || 'pri_01abc123max_annual',
  }
};

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  
  // Get initial state from navigation OR URL query params (for cross-domain)
  // Query params are used when coming from noblesyntax.com
  const searchParams = new URLSearchParams(location.search);
  const initialPlan = location.state?.selectedPlan || searchParams.get('plan') || 'pro';
  const initialBilling = location.state?.billingType || searchParams.get('billing') || 'annual';
  
  // State
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [isAnnualBilling, setIsAnnualBilling] = useState(initialBilling === 'annual');
  const [currentStep, setCurrentStep] = useState('plan');
  const [isLogin, setIsLogin] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paddleReady, setPaddleReady] = useState(false);
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // User info
  const [userId, setUserId] = useState(null);
  
  // Active subscription state - prevents re-purchasing
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  
  // Plans data
  const plans = {
    standard: {
      name: 'Standard',
      icon: Database,
      color: '#2B8AFF',
      annualPrice: 10,
      monthlyPrice: 15,
      annualTotal: 120,
      projects: '1',
      projectsLabel: 'projects per month',
      features: [
        'Full data quality analysis',
        '8 quality dimension checks',
        'Interactive dashboards',
        'CSV & Excel exports',
        'Email support'
      ]
    },
    pro: {
      name: 'Pro',
      icon: Zap,
      color: '#8B5CF6',
      annualPrice: 15,
      monthlyPrice: 20,
      annualTotal: 180,
      projects: '3',
      projectsLabel: 'projects per month',
      popular: true,
      features: [
        'Everything in Standard',
        'AI-powered anomaly detection',
        'Back-check validation',
        'Custom quality thresholds',
        'Priority support'
      ]
    },
    max: {
      name: 'Max',
      icon: Infinity,
      color: '#10B981',
      annualPrice: 80,
      monthlyPrice: 100,
      annualTotal: 960,
      projects: 'Unlimited',
      projectsLabel: 'projects',
      features: [
        'Everything in Pro',
        'Unlimited team members',
        'API access',
        'Custom integrations',
        'Dedicated account manager'
      ]
    }
  };


  const currentPlan = plans[selectedPlan];
  const currentPrice = isAnnualBilling ? currentPlan.annualPrice : currentPlan.monthlyPrice;
  const billingPeriod = isAnnualBilling ? 'year' : 'month';

  // ============================================================================
  // PADDLE INITIALIZATION
  // ============================================================================

  // Load Outfit font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) {
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    // Load Paddle.js
    const loadPaddle = async () => {
      // Check if already loaded
      if (window.Paddle) {
        initializePaddle();
        return;
      }

      // Load script
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      
      script.onload = () => {
        initializePaddle();
      };
      
      script.onerror = () => {
        console.error('Failed to load Paddle.js');
        setError('Payment system failed to load. Please refresh the page.');
      };
      
      document.body.appendChild(script);
    };

    const initializePaddle = () => {
      if (!window.Paddle) return;

      try {
        // Set environment
        if (PADDLE_ENVIRONMENT === 'sandbox') {
          window.Paddle.Environment.set('sandbox');
        }

        // Initialize with client token
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: handlePaddleEvent,
        });

        setPaddleReady(true);
        console.log('Paddle initialized successfully');
      } catch (err) {
        console.error('Failed to initialize Paddle:', err);
        setError('Payment system initialization failed.');
      }
    };

    loadPaddle();
  }, []);

  // ============================================================================
  // PADDLE EVENT HANDLER
  // ============================================================================

  const handlePaddleEvent = useCallback((event) => {
    console.log('Paddle event:', event);

    switch (event.name) {
      case 'checkout.loaded':
        console.log('Checkout loaded');
        break;

      case 'checkout.completed':
        console.log('Checkout completed:', event.data);
        handleCheckoutComplete(event.data);
        break;

      case 'checkout.closed':
        console.log('Checkout closed');
        setIsProcessing(false);
        break;

      case 'checkout.error':
        console.error('Checkout error:', event.data);
        setError('Payment failed. Please try again.');
        setIsProcessing(false);
        break;

      case 'checkout.customer.created':
        console.log('Customer created:', event.data);
        break;

      case 'checkout.payment.selected':
        console.log('Payment method selected:', event.data);
        break;

      default:
        break;
    }
  }, []);

  // ============================================================================
  // CHECKOUT HANDLERS
  // ============================================================================

  const handleCheckoutComplete = async (data) => {
    console.log('Processing checkout completion:', data);

    try {
      // Link Paddle customer to our user
      const customerId = data.customer?.id;
      
      if (customerId && userId) {
        await linkPaddleCustomer(customerId);
      }

      // Navigate to success
      setCurrentStep('success');
      setIsProcessing(false);

    } catch (err) {
      console.error('Error processing checkout completion:', err);
      // Still show success - webhook will handle subscription activation
      setCurrentStep('success');
      setIsProcessing(false);
    }
  };

  const linkPaddleCustomer = async (paddleCustomerId) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE}/api/billing/link-paddle-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paddle_customer_id: paddleCustomerId })
      });

      if (!response.ok) {
        throw new Error('Failed to link customer');
      }

      console.log('Paddle customer linked successfully');
    } catch (err) {
      console.error('Error linking Paddle customer:', err);
      // Non-fatal - webhook will still process
    }
  };

  const initiatePayment = () => {
    if (!paddleReady) {
      setError('Payment system is not ready. Please wait...');
      return;
    }

    setError('');
    setIsProcessing(true);

    const priceId = PADDLE_PRICES[selectedPlan][isAnnualBilling ? 'annual' : 'monthly'];

    console.log('Opening checkout with priceId:', priceId, 'Plan:', selectedPlan, 'Annual:', isAnnualBilling);


    try {
      window.Paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: {
          email: email,
        },
        customData: {
          user_id: userId,
          plan: selectedPlan,
          billing_cycle: isAnnualBilling ? 'annual' : 'monthly',
        },
        settings: {
          displayMode: 'overlay',
          theme: isDarkMode ? 'dark' : 'light',
          locale: 'en',
          allowLogout: false,
          showAddDiscounts: true,
          successUrl: `${window.location.origin}/subscription/success?plan=${selectedPlan}&billing=${isAnnualBilling ? 'annual' : 'monthly'}`,
        },
      });
    } catch (err) {
      console.error('Error opening checkout:', err);
      setError('Failed to open checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userInfo = localStorage.getItem('userInfo');
    
    if (token && userInfo) {
      setIsAuthenticated(true);
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        setEmail(parsedUserInfo.email || '');
        setFullName(parsedUserInfo.name || '');
        setUserId(parsedUserInfo.id || null);
      } catch (e) {
        console.error('Error parsing user info:', e);
      }
    }
  }, []);

  // Check if user already has an active subscription
  useEffect(() => {
    const checkSubscription = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/billing/subscription`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Subscription status:', data);
          
          // Check if user has active or trialing subscription
          if (data.subscription_status === 'active' || data.subscription_status === 'trialing') {
            setHasActiveSubscription(true);
            setCurrentSubscription(data);
            setCurrentStep('manage'); // Switch to management view
          }
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
        // Continue with normal flow if check fails
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = `${API_BASE}/${isLogin ? 'login' : 'register'}`;
      const body = isLogin 
        ? { email, password, subscription_mode: true }
        : { email, password, name: fullName.trim() };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      const userInfo = {
        id: data.user_id || data.id,
        name: data.name,
        email: data.email,
        picture: null
      };

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      setFullName(data.name);
      setUserId(userInfo.id);
      setIsAuthenticated(true);
      setCurrentStep('payment');
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToProjects = () => {
    navigate('/projects');
  };

  // ============================================================================
  // STYLES
  // ============================================================================

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F9FAFB',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    backgroundDecor: {
      position: 'absolute',
      top: '-50%',
      right: '-25%',
      width: '80%',
      height: '150%',
      background: `radial-gradient(ellipse at center, ${isDarkMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.05)'} 0%, transparent 70%)`,
      pointerEvents: 'none',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 40px',
      position: 'relative',
      zIndex: 10,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.2s',
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    logoImage: {
      width: '32px',
      height: '32px',
    },
    logoText: {
      fontSize: '20px',
      fontWeight: '700',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px 40px 60px',
      position: 'relative',
      zIndex: 10,
    },
    pageTitle: {
      fontSize: '32px',
      fontWeight: '700',
      textAlign: 'center',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
      marginBottom: '8px',
    },
    pageSubtitle: {
      fontSize: '16px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
      textAlign: 'center',
      marginBottom: '32px',
    },
    stepsContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '24px',
      marginBottom: '40px',
    },
    step: (isActive, isCompleted) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      opacity: isActive || isCompleted ? 1 : 0.5,
    }),
    stepCircle: (isActive, isCompleted) => ({
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isCompleted ? '#10B981' : isActive ? '#6366F1' : isDarkMode ? '#374151' : '#E5E7EB',
      color: isCompleted || isActive ? 'white' : isDarkMode ? '#6D6E74' : '#6B7280',
      fontSize: '14px',
      fontWeight: '600',
    }),
    stepLabel: {
      fontSize: '14px',
      color: isDarkMode ? '#D1D5DB' : '#4B5563',
      fontWeight: '500',
    },
    billingToggle: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '32px',
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '6px',
      backgroundColor: isDarkMode ? '#1F2937' : '#7D7D81',
      borderRadius: '12px',
    },
    toggleOption: (isActive) => ({
      padding: '10px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: isActive ? (isDarkMode ? '#374151' : 'white') : 'transparent',
      color: isActive ? (isDarkMode ? '#7D7D81' : '#1F2937') : (isDarkMode ? '#6D6E74' : '#6B7280'),
      boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
    }),
    saveBadge: {
      backgroundColor: '#10B981',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      marginLeft: '8px',
    },
    plansGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '24px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    planCard: (isSelected, color) => ({
      padding: '24px',
      borderRadius: '16px',
      backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
      border: isSelected 
        ? `2px solid ${color}` 
        : `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative',
      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
    }),
    popularBadge: {
      position: 'absolute',
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#8B5CF6',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    planIcon: (color) => ({
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      backgroundColor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px',
    }),
    planName: {
      fontSize: '20px',
      fontWeight: '700',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
      marginBottom: '4px',
    },
    planPrice: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '4px',
      marginBottom: '8px',
    },
    priceAmount: {
      fontSize: '36px',
      fontWeight: '700',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
    },
    pricePeriod: {
      fontSize: '14px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
    },
    planProjects: {
      fontSize: '14px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
      marginBottom: '16px',
    },
    planFeatures: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    planFeature: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      color: isDarkMode ? '#D1D5DB' : '#4B5563',
    },
    continueButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      padding: '14px',
      marginTop: '24px',
      backgroundColor: '#6366F1',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    authContainer: {
      maxWidth: '440px',
      margin: '0 auto',
      padding: '32px',
      backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
      borderRadius: '16px',
      border: `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
    },
    authTabs: {
      display: 'flex',
      gap: '8px',
      marginBottom: '24px',
    },
    authTab: (isActive) => ({
      flex: 1,
      padding: '12px',
      textAlign: 'center',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: isActive ? (isDarkMode ? '#374151' : '#7D7D81') : 'transparent',
      color: isActive ? (isDarkMode ? '#7D7D81' : '#1F2937') : (isDarkMode ? '#6D6E74' : '#6B7280'),
    }),
    inputGroup: {
      position: 'relative',
      marginBottom: '16px',
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: isDarkMode ? '#6B7280' : '#6D6E74',
    },
    input: {
      width: '100%',
      padding: '14px 14px 14px 44px',
      borderRadius: '10px',
      border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F9FAFB',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
    },
    submitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      padding: '14px',
      backgroundColor: '#6366F1',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    errorMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px',
      borderRadius: '8px',
      backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
      color: '#EF4444',
      fontSize: '14px',
      marginBottom: '16px',
    },
    paymentContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 360px',
      gap: '32px',
      maxWidth: '900px',
      margin: '0 auto',
    },
    paymentMain: {
      padding: '32px',
      backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
      borderRadius: '16px',
      border: `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
    },
    orderSummary: {
      padding: '24px',
      backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
      borderRadius: '16px',
      border: `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
      height: 'fit-content',
    },
    summaryTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
      marginBottom: '20px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
    },
    summaryLabel: {
      fontSize: '14px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
    },
    summaryValue: {
      fontSize: '14px',
      fontWeight: '500',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
    },
    summaryDivider: {
      height: '1px',
      backgroundColor: isDarkMode ? '#374151' : '#E5E7EB',
      margin: '16px 0',
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '18px',
      fontWeight: '700',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
    },
    trialNote: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '16px',
      padding: '12px',
      backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : '#ECFDF5',
      borderRadius: '8px',
      color: '#10B981',
      fontSize: '13px',
    },
    securityBadges: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      marginTop: '20px',
    },
    securityBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      color: isDarkMode ? '#6B7280' : '#6D6E74',
    },
    successContainer: {
      maxWidth: '500px',
      margin: '60px auto',
      textAlign: 'center',
      padding: '40px',
      backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
      borderRadius: '20px',
      border: `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      color: '#10B981',
    },
    successTitle: {
      fontSize: '28px',
      fontWeight: '700',
      color: isDarkMode ? '#7D7D81' : '#1F2937',
      marginBottom: '12px',
    },
    successSubtitle: {
      fontSize: '16px',
      color: isDarkMode ? '#6D6E74' : '#6B7280',
      marginBottom: '32px',
      lineHeight: '1.6',
    },
    successButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '14px 28px',
      backgroundColor: '#6366F1',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    globalBadge: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.1)' : '#EEF2FF',
      borderRadius: '20px',
      margin: '0 auto 24px',
      width: 'fit-content',
    },
    globalBadgeText: {
      fontSize: '13px',
      color: '#6366F1',
      fontWeight: '500',
    },
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderPlanSelection = () => (
    <>
      <div style={styles.globalBadge}>
        <Globe size={16} color="#6366F1" />
        <span style={styles.globalBadgeText}>Payments accepted globally • Tax handled automatically</span>
      </div>

      <div style={styles.billingToggle}>
        <div style={styles.toggleContainer}>
          <div
            style={styles.toggleOption(!isAnnualBilling)}
            onClick={() => setIsAnnualBilling(false)}
          >
            Monthly
          </div>
          <div
            style={styles.toggleOption(isAnnualBilling)}
            onClick={() => setIsAnnualBilling(true)}
          >
            Annual
            <span style={styles.saveBadge}>Save 20%</span>
          </div>
        </div>
      </div>

      <div style={styles.plansGrid}>
        {Object.entries(plans).map(([key, plan]) => {
          const PlanIcon = plan.icon;
          const isSelected = selectedPlan === key;
          const displayPrice = isAnnualBilling ? plan.annualPrice : plan.monthlyPrice;
          
          return (
            <div
              key={key}
              style={styles.planCard(isSelected, plan.color)}
              onClick={() => setSelectedPlan(key)}
            >
              {plan.popular && (
                <div style={styles.popularBadge}>
                  <Star size={12} fill="white" />
                  Most Popular
                </div>
              )}
              
              <div style={styles.planIcon(plan.color)}>
                <PlanIcon size={24} color={plan.color} />
              </div>
              
              <div style={styles.planName}>{plan.name}</div>
              
              <div style={styles.planPrice}>
                <span style={styles.priceAmount}>${displayPrice}</span>
                <span style={styles.pricePeriod}>/month</span>
              </div>
              
              {isAnnualBilling && (
                <div style={{ fontSize: '12px', color: '#10B981', marginBottom: '8px' }}>
                  ${plan.annualTotal} billed annually
                </div>
              )}
              
              <div style={styles.planProjects}>
                <strong>{plan.projects}</strong> {plan.projectsLabel}
              </div>
              
              <div style={styles.planFeatures}>
                {plan.features.map((feature, idx) => (
                  <div key={idx} style={styles.planFeature}>
                    <Check size={14} color={plan.color} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: plan.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Check size={14} color="white" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button
          style={styles.continueButton}
          onClick={() => setCurrentStep(isAuthenticated ? 'payment' : 'auth')}
        >
          <span>Continue with {currentPlan.name}</span>
          <ChevronRight size={20} />
        </button>
        
        <p style={{ 
          marginTop: '16px', 
          fontSize: '13px', 
          color: isDarkMode ? '#6B7280' : '#6D6E74' 
        }}>
          15-day free trial • No credit card required to start • Cancel anytime
        </p>
      </div>
    </>
  );

  const renderAuth = () => (
    <div style={styles.authContainer}>
      <div style={styles.authTabs}>
        <div
          style={styles.authTab(isLogin)}
          onClick={() => { setIsLogin(true); setError(''); }}
        >
          Sign In
        </div>
        <div
          style={styles.authTab(!isLogin)}
          onClick={() => { setIsLogin(false); setError(''); }}
        >
          Create Account
        </div>
      </div>

      {error && (
        <div style={styles.errorMessage}>
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleAuth}>
        {!isLogin && (
          <div style={styles.inputGroup}>
            <User size={20} style={styles.inputIcon} />
            <input
              type="text"
              placeholder="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={styles.input}
              required={!isLogin}
            />
          </div>
        )}

        <div style={styles.inputGroup}>
          <Mail size={20} style={styles.inputIcon} />
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.inputGroup}>
          <Lock size={20} style={styles.inputIcon} />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ ...styles.input, paddingRight: '44px' }}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isDarkMode ? '#6B7280' : '#6D6E74',
            }}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <button
          type="submit"
          style={{
            ...styles.submitButton,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          disabled={loading}
        >
          {loading ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <span>Processing...</span>
            </>
          ) : (
            <span>{isLogin ? 'Sign In & Continue' : 'Create Account & Continue'}</span>
          )}
        </button>
      </form>
    </div>
  );

  const renderPayment = () => (
    <div style={styles.paymentContainer}>
      <div style={styles.paymentMain}>
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          marginBottom: '8px',
          color: isDarkMode ? '#7D7D81' : '#1F2937'
        }}>
          Complete Your Subscription
        </h3>
        <p style={{ 
          fontSize: '14px', 
          color: isDarkMode ? '#6D6E74' : '#6B7280',
          marginBottom: '24px'
        }}>
          Secure payment powered by Paddle
        </p>

        {error && (
          <div style={styles.errorMessage}>
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        <div style={{
          padding: '16px',
          backgroundColor: isDarkMode ? '#0D0E0E' : '#F9FAFB',
          borderRadius: '12px',
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: `${currentPlan.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <currentPlan.icon size={20} color={currentPlan.color} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: isDarkMode ? '#7D7D81' : '#1F2937' }}>
                DataQuad {currentPlan.name}
              </div>
              <div style={{ fontSize: '13px', color: isDarkMode ? '#6D6E74' : '#6B7280' }}>
                {isAnnualBilling ? 'Annual' : 'Monthly'} subscription
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods Info */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ 
            fontSize: '13px', 
            color: isDarkMode ? '#6D6E74' : '#6B7280',
            marginBottom: '12px'
          }}>
            Accepted payment methods:
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['Visa', 'Mastercard', 'Amex', 'PayPal', 'Apple Pay', 'Google Pay'].map(method => (
              <span key={method} style={{
                padding: '6px 12px',
                backgroundColor: isDarkMode ? '#1F2937' : '#7D7D81',
                borderRadius: '6px',
                fontSize: '12px',
                color: isDarkMode ? '#D1D5DB' : '#4B5563',
              }}>
                {method}
              </span>
            ))}
          </div>
        </div>

        {/* Pay Button */}
        <button
          onClick={initiatePayment}
          style={{
            ...styles.submitButton,
            opacity: isProcessing || !paddleReady ? 0.6 : 1,
            cursor: isProcessing || !paddleReady ? 'not-allowed' : 'pointer',
          }}
          disabled={isProcessing || !paddleReady}
        >
          {isProcessing ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <span>Processing...</span>
            </>
          ) : !paddleReady ? (
            <span>Loading payment...</span>
          ) : (
            <>
              <Lock size={18} />
              <span>Start Free Trial</span>
            </>
          )}
        </button>

        {/* Security badges */}
        <div style={styles.securityBadges}>
          <div style={styles.securityBadge}>
            <Shield size={14} />
            <span>256-bit SSL</span>
          </div>
          <div style={styles.securityBadge}>
            <Lock size={14} />
            <span>PCI Compliant</span>
          </div>
          <div style={styles.securityBadge}>
            <Globe size={14} />
            <span>Global Tax Handled</span>
          </div>
        </div>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '12px', 
          color: isDarkMode ? '#6B7280' : '#6D6E74', 
          marginTop: '16px' 
        }}>
          Secure payment by Paddle • Cancel anytime
        </p>
      </div>

      {renderOrderSummary()}
    </div>
  );

  const renderOrderSummary = () => (
    <div style={styles.orderSummary}>
      <h3 style={styles.summaryTitle}>Order Summary</h3>
      
      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Plan</span>
        <span style={styles.summaryValue}>{currentPlan.name}</span>
      </div>
      
      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Billing</span>
        <span style={styles.summaryValue}>{isAnnualBilling ? 'Annual' : 'Monthly'}</span>
      </div>
      
      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Projects</span>
        <span style={styles.summaryValue}>{currentPlan.projects} {currentPlan.projectsLabel}</span>
      </div>

      <div style={styles.summaryDivider} />

      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Due today</span>
        <span style={{ ...styles.summaryValue, color: '#10B981' }}>$0.00</span>
      </div>

      <div style={styles.summaryTotal}>
        <span>After trial</span>
        <span>${currentPrice}/{isAnnualBilling ? 'mo' : 'month'}</span>
      </div>

      {isAnnualBilling && (
        <div style={{ 
          fontSize: '12px', 
          color: isDarkMode ? '#6D6E74' : '#6B7280',
          textAlign: 'right',
          marginTop: '4px'
        }}>
          (${currentPlan.annualTotal}/year)
        </div>
      )}

      <div style={styles.trialNote}>
        <Clock size={16} />
        <span>15-day free trial included</span>
      </div>

      <div style={{ marginTop: '20px' }}>
        <p style={{ 
          fontSize: '12px', 
          color: isDarkMode ? '#6B7280' : '#6D6E74', 
          marginBottom: '8px' 
        }}>
          Includes:
        </p>
        {currentPlan.features.slice(0, 3).map((feature, idx) => (
          <div key={idx} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '6px' 
          }}>
            <Check size={14} color={currentPlan.color} />
            <span style={{ 
              fontSize: '13px', 
              color: isDarkMode ? '#CBD5E1' : '#4B5563' 
            }}>
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div style={styles.successContainer}>
      <div style={styles.successIcon}>
        <CheckCircle2 size={40} />
      </div>
      <h2 style={styles.successTitle}>You're All Set!</h2>
      <p style={styles.successSubtitle}>
        Welcome to DataQuad {currentPlan.name}! Your 15-day free trial has started. 
        You have access to {currentPlan.projects} {currentPlan.projectsLabel} and all {currentPlan.name} features.
      </p>
      <button style={styles.successButton} onClick={goToProjects}>
        <Sparkles size={18} />
        <span>Start Your First Project</span>
        <ChevronRight size={20} />
      </button>
      
      <p style={{ 
        marginTop: '24px', 
        fontSize: '13px', 
        color: isDarkMode ? '#6B7280' : '#6D6E74' 
      }}>
        You'll receive a confirmation email shortly.
      </p>
    </div>
  );

  const renderManageSubscription = () => {
    const subPlan = currentSubscription?.subscription_plan || 'pro';
    const subStatus = currentSubscription?.subscription_status || 'active';
    const planInfo = plans[subPlan] || plans.pro;
    const PlanIcon = planInfo.icon;
    
    const isTrialing = subStatus === 'trialing';
    const trialEnd = currentSubscription?.trial_end_date 
      ? new Date(currentSubscription.trial_end_date).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      : null;

    return (
      <div style={{
        maxWidth: '600px',
        margin: '40px auto',
        padding: '40px',
        backgroundColor: isDarkMode ? '#1A1B1B' : 'white',
        borderRadius: '20px',
        border: `1px solid ${isDarkMode ? '#2D2D2D' : '#E5E7EB'}`,
        textAlign: 'center',
      }}>
        {/* Current Plan Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: `${planInfo.color}15`,
          borderRadius: '20px',
          marginBottom: '24px',
        }}>
          <PlanIcon size={18} color={planInfo.color} />
          <span style={{ color: planInfo.color, fontWeight: '600', fontSize: '14px' }}>
            {isTrialing ? 'Trial Active' : 'Active Subscription'}
          </span>
        </div>

        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: isDarkMode ? '#7D7D81' : '#1F2937',
          marginBottom: '8px',
        }}>
          You're on DataQuad {planInfo.name}
        </h2>

        {isTrialing && trialEnd && (
          <p style={{
            fontSize: '14px',
            color: '#F59E0B',
            backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.1)',
            padding: '8px 16px',
            borderRadius: '8px',
            display: 'inline-block',
            marginBottom: '16px',
          }}>
            <Clock size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
            Trial ends on {trialEnd}
          </p>
        )}

        <p style={{
          fontSize: '16px',
          color: isDarkMode ? '#6D6E74' : '#6B7280',
          marginBottom: '32px',
          lineHeight: '1.6',
        }}>
          You have access to {planInfo.projects} {planInfo.projectsLabel} and all {planInfo.name} features.
        </p>

        {/* Features List */}
        <div style={{
          backgroundColor: isDarkMode ? '#0D0E0E' : '#F9FAFB',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '32px',
          textAlign: 'left',
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: isDarkMode ? '#6D6E74' : '#6B7280',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Your Plan Includes
          </h4>
          {planInfo.features.map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: index < planInfo.features.length - 1 ? '12px' : 0,
            }}>
              <Check size={16} color={planInfo.color} />
              <span style={{ fontSize: '14px', color: isDarkMode ? '#CBD5E1' : '#4B5563' }}>
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={goToProjects}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 28px',
              backgroundColor: planInfo.color,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <Sparkles size={18} />
            Go to DataQuad App
            <ChevronRight size={18} />
          </button>

          {/* Upgrade/Downgrade Option */}
          {subPlan !== 'max' && (
            <button
              onClick={() => {
                setHasActiveSubscription(false);
                setCurrentStep('plan');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px 28px',
                backgroundColor: isDarkMode ? '#2D2D2D' : '#7D7D81',
                color: isDarkMode ? '#E5E7EB' : '#374151',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Zap size={16} />
              Upgrade Plan
            </button>
          )}

          {/* Manage in Paddle */}
          {currentSubscription?.paddle_customer_id && (
            <a
              href={PADDLE_ENVIRONMENT === 'sandbox' ? 'https://sandbox-customer-portal.paddle.com' : 'https://customer-portal.paddle.com'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: isDarkMode ? '#6D6E74' : '#6B7280',
                fontSize: '14px',
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
            >
              <CreditCard size={16} />
              Manage Billing & Invoices
            </a>
          )}
        </div>

        {/* Help Text */}
        <p style={{
          marginTop: '24px',
          fontSize: '13px',
          color: isDarkMode ? '#6B7280' : '#6D6E74',
        }}>
          Need to cancel or change your subscription? Visit the billing portal above.
        </p>
      </div>
    );
  };

  const getStepStatus = (step) => {
    const steps = ['plan', 'auth', 'payment', 'success'];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  // Show loading while checking subscription status
  if (checkingSubscription) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundDecor} />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '16px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid',
            borderColor: isDarkMode ? '#2D2D2D' : '#E5E7EB',
            borderTopColor: '#6366F1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ 
            color: isDarkMode ? '#6D6E74' : '#6B7280', 
            fontSize: '14px' 
          }}>
            Checking subscription status...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // If user has active subscription, show management view
  if (hasActiveSubscription && currentStep === 'manage') {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundDecor} />
        
        <div style={styles.header}>
          <Link to="/" style={styles.backButton}>
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </Link>
          <div style={styles.logoSection}>
            <img src={logoSource} alt="dataQuad" style={styles.logoImage} />
            <span style={styles.logoText}>dataQuad</span>
          </div>
          <div style={{ width: '120px' }} />
        </div>

        <div style={styles.mainContent}>
          <h1 style={styles.pageTitle}>Your Subscription</h1>
          <p style={styles.pageSubtitle}>Manage your DataQuad subscription</p>
          {renderManageSubscription()}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundDecor} />
      
      <div style={styles.header}>
        <Link to="/" style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>Back to Home</span>
        </Link>
        <div style={styles.logoSection}>
          <img src={logoSource} alt="dataQuad" style={styles.logoImage} />
          <span style={styles.logoText}>dataQuad</span>
        </div>
        <div style={{ width: '120px' }} />
      </div>

      <div style={styles.mainContent}>
        {currentStep !== 'success' && (
          <>
            <h1 style={styles.pageTitle}>
              {currentStep === 'plan' ? 'Choose Your Plan' : 
               currentStep === 'auth' ? 'Create Your Account' : 
               'Complete Your Subscription'}
            </h1>
            <p style={styles.pageSubtitle}>
              {currentStep === 'plan' ? 'Select the plan that best fits your research needs' :
               currentStep === 'auth' ? 'Sign in or create an account to continue' :
               'Start your 15-day free trial'}
            </p>

            <div style={styles.stepsContainer}>
              {[
                { key: 'plan', label: 'Select Plan', num: '1' },
                { key: 'auth', label: 'Account', num: '2' },
                { key: 'payment', label: 'Payment', num: '3' }
              ].map((step) => {
                const status = getStepStatus(step.key);
                return (
                  <div key={step.key} style={styles.step(status === 'active', status === 'completed')}>
                    <div style={styles.stepCircle(status === 'active', status === 'completed')}>
                      {status === 'completed' ? <Check size={18} /> : step.num}
                    </div>
                    <span style={styles.stepLabel}>{step.label}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {currentStep === 'plan' && renderPlanSelection()}
        {currentStep === 'auth' && renderAuth()}
        {currentStep === 'payment' && renderPayment()}
        {currentStep === 'success' && renderSuccess()}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input:focus {
          border: 1px solid #6366F1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
        }
        button:hover:not(:disabled) {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default Subscription;